import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Globe, Wifi, Database, Clock, ChevronDown, ChevronUp, ShoppingCart, X } from 'lucide-react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useTranslation } from '../useTranslation';

const CountryDetails = ({ user, setShowAuthModal }) => {
  const { t } = useTranslation();

  const { countryCode } = useParams();
  const [searchParams] = useSearchParams();
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPlanDetailsModal, setShowPlanDetailsModal] = useState(false);
  const [planDetailsModalData, setPlanDetailsModalData] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [filterDuration, setFilterDuration] = useState('all');

  // Get country code
  const targetCountryCode = countryCode || searchParams.get('country');

  // Text shadow style (unchanged UI)
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .shadow-text {
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    if (targetCountryCode) loadCountryData(targetCountryCode);
  }, [targetCountryCode]);

  // Auto-select first plan
  useEffect(() => {
    if (country) {
      const allPlans = getCountryPlans(country);
      const filtered = filterPlansByDuration(allPlans);
      const sorted = sortPlans(filtered);
      if (sorted.length > 0) setSelectedPlan(sorted[0]);
    }
  }, [country, filterDuration, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCountryData = async (code) => {
    try {
      setLoading(true);
      setError(null);

      const docRef = doc(db, 'countries', code.toUpperCase());
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const countryData = { iso: docSnap.id, ...docSnap.data() };
        setCountry(countryData);
      } else {
        setError(t('countryDetails.errors.notFound'));
      }

      setLoading(false);
    } catch (e) {
      console.error('Error fetching country data:', e);
      setError(t('countryDetails.errors.loadFail'));
      setLoading(false);
    }
  };

  // Build plans list with pricing
  const getCountryPlans = (country) => {
    if (!country) return [];
    const plans = [];
    const markup = country.markup_percentage || 25;

    // fixed plans
    Object.entries(country.fixed_plans || {}).forEach(([duration, durationPlans]) => {
      durationPlans.forEach((plan) => {
        const finalPrice = plan.price * (1 + markup / 100);
        plans.push({
          ...plan,
          country: country.name,
          countryCode: country.iso,
          duration: parseInt(duration),
          finalPrice,
          isUnlimited: false,
          type: 'fixed',
          bundle_name: plan.name,
          country_code: country.iso,
          data_amount: plan.data_amount,
          auto_start: plan.autostart,
        });
      });
    });

    // unlimited plans
    Object.entries(country.unlimited_plans || {}).forEach(([duration, durationPlans]) => {
      durationPlans.forEach((plan) => {
        const finalPrice = plan.price * (1 + markup / 100);
        plans.push({
          ...plan,
          country: country.name,
          countryCode: country.iso,
          duration: parseInt(duration),
          finalPrice,
          isUnlimited: true,
          type: 'unlimited',
          bundle_name: plan.name,
          country_code: country.iso,
          data_amount: -1,
          auto_start: plan.autostart,
        });
      });
    });

    return plans;
  };

  const filterPlansByDuration = (plans) => {
    if (filterDuration === 'all') return plans;
    return plans.filter((plan) => plan.duration === parseInt(filterDuration));
  };

  // Sorting (unchanged UI/logic)
  const sortPlans = (plans) => {
    return [...plans].sort((a, b) => {
      const getPriority = (plan) => {
        if (plan.isUnlimited && plan.duration === 1) return 1;
        if (plan.isUnlimited && plan.duration === 3) return 2;
        if (plan.isUnlimited && plan.duration === 5) return 3;
        if (!plan.isUnlimited && plan.duration === 7) return 4;
        // Leave remainder to secondary sorting below
        return 999;
      };

      const priorityA = getPriority(a);
      const priorityB = getPriority(b);

      if (priorityA !== priorityB) return priorityA - priorityB;

      switch (sortBy) {
        case 'price':
          return a.finalPrice - b.finalPrice;
        case 'data':
          if (a.isUnlimited && !b.isUnlimited) return -1;
          if (!a.isUnlimited && b.isUnlimited) return 1;
          if (a.isUnlimited && b.isUnlimited) return 0;
          return b.data_amount - a.data_amount;
        case 'duration':
          return a.duration - b.duration;
        default:
          return a.name?.localeCompare(b.name) || 0;
      }
    });
  };

  const handlePlanDetails = (plan, event) => {
    event.stopPropagation();
    setPlanDetailsModalData(plan);
    setShowPlanDetailsModal(true);
  };

  const handlePlanSelection = (plan) => setSelectedPlan(plan);

  const handlePurchase = (plan) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    window.location.href = `/checkout?plan=${encodeURIComponent(
      JSON.stringify({
        id: plan.bundle_name || plan.name,
        country: country.name,
        countryCode: country.iso,
        countryFlag: country.image_url,
        dataAmount: plan.isUnlimited ? t('countryDetails.plan.unlimited') : `${(plan.data_amount / 1000).toFixed(1)} ${t('countryDetails.plan.gb')}`,
        duration: plan.duration,
        price: plan.finalPrice.toFixed(2),
        planData: plan,
      })
    )}`;
  };

  const PlanDetailsModal = () => {
    if (!planDetailsModalData) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-lg w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">
              {t('countryDetails.modal.title')}
            </h3>
            <button
              onClick={() => setShowPlanDetailsModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Type Header */}
            <div
              className={`p-4 rounded-lg ${
                planDetailsModalData.isUnlimited
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600'
                  : 'bg-gradient-to-r from-blue-500 to-blue-600'
              } text-white`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {planDetailsModalData.isUnlimited ? (
                    <Wifi className="w-5 h-5 mr-2" />
                  ) : (
                    <Database className="w-5 h-5 mr-2" />
                  )}
                  <span className="font-bold text-lg">
                    {planDetailsModalData.isUnlimited
                      ? t('countryDetails.plan.unlimitedData')
                      : `${(planDetailsModalData.data_amount / 1000).toFixed(1)}${t('countryDetails.plan.gbShort')} ${t('countryDetails.plan.data')}`}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold">
                    ${planDetailsModalData.finalPrice.toFixed(2)}
                  </div>
                  <div className="text-xs opacity-90">{t('countryDetails.currency.usd')}</div>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('countryDetails.modal.country')}</span>
                <span className="font-semibold text-gray-900">{planDetailsModalData.country}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('countryDetails.modal.duration')}</span>
                <span className="font-semibold text-gray-900">
                  {t('countryDetails.duration.days', { count: planDetailsModalData.duration })}
                </span>
              </div>
              {planDetailsModalData.speed && planDetailsModalData.speed.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('countryDetails.modal.network')}</span>
                  <span className="font-semibold text-gray-900">
                    {planDetailsModalData.speed.join(', ')}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">{t('countryDetails.modal.autostart')}</span>
                <span
                  className={`font-semibold ${
                    planDetailsModalData.auto_start ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {planDetailsModalData.auto_start
                    ? t('countryDetails.modal.enabled')
                    : t('countryDetails.modal.disabled')}
                </span>
              </div>
              {planDetailsModalData.groups && planDetailsModalData.groups.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('countryDetails.modal.planType')}</span>
                  <span className="font-semibold text-gray-900 text-xs">
                    {planDetailsModalData.groups.join(', ')}
                  </span>
                </div>
              )}
            </div>

            {/* Description */}
            {planDetailsModalData.description && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">{t('countryDetails.modal.description')}</h4>
                <p className="text-sm text-blue-800">{planDetailsModalData.description}</p>
              </div>
            )}

            {/* CTA */}
            <button
              onClick={() => {
                setShowPlanDetailsModal(false);
                handlePurchase(planDetailsModalData);
              }}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 px-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              {user ? t('countryDetails.modal.purchase') : t('countryDetails.modal.signInToPurchase')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('countryDetails.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !country) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md mx-auto px-6">
          <Globe className="w-16 h-16 text-gray-300 mx-auto" />
          <h2 className="text-2xl font-bold text-gray-900">
            {t('countryDetails.notFound.title')}
          </h2>
          <p className="text-gray-600">
            {error || t('countryDetails.notFound.subtitle')}
          </p>
          <Link
            to="/browse"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('countryDetails.notFound.cta')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back */}
        <div className="mb-4 md:mb-6">
          <Link to="/browse" className="flex items-center text-[#7B3EF0] hover:underline">
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t('countryDetails.back')}
          </Link>
        </div>

        {/* Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 mb-4 md:mb-8">
          {/* Left: Image + blurb */}
          <div className="relative">
            <h1 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">{country.name}</h1>
            <div className="aspect-[4/3] rounded-xl md:rounded-2xl overflow-hidden shadow-lg md:shadow-2xl">
              {country.image_url ? (
                <img src={country.image_url} alt={country.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 flex items-center justify-center">
                  <Globe className="w-16 md:w-24 h-16 md:h-24 text-white opacity-80" />
                </div>
              )}
            </div>

            <div className="mt-4 md:mt-6">
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              {t('countryDetails.blurb').replace('{{country}}', country?.name ?? '')}
            </p>

            </div>
          </div>

          {/* Right: Plans */}
          <div className="space-y-4 md:space-y-6">
            <div className="space-y-3 md:space-y-4">
              {(() => {
                const allPlans = getCountryPlans(country);
                const filteredPlans = filterPlansByDuration(allPlans);
                const sortedPlans = sortPlans(filteredPlans);

                if (sortedPlans.length === 0) {
                  return (
                    <div className="text-center py-6 md:py-8">
                      <Globe className="w-10 md:w-12 h-10 md:h-12 text-gray-300 mx-auto mb-3 md:mb-4" />
                      <p className="text-sm md:text-base text-gray-500">
                        {t('countryDetails.noPlans')}
                      </p>
                    </div>
                  );
                }

                const plansToShow = showAllPlans ? sortedPlans : sortedPlans.slice(0, 6);

                return plansToShow.map((plan, index) => (
                  <div
                    key={`${plan.bundle_name || plan.name}-${plan.duration}-${index}`}
                    className={`flex items-center justify-between p-3 md:p-4 rounded-lg md:rounded-xl border transition-all duration-300 cursor-pointer ${
                      selectedPlan &&
                      selectedPlan.bundle_name === plan.bundle_name &&
                      selectedPlan.duration === plan.duration
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 hover:border-blue-300 bg-white hover:shadow-sm'
                    }`}
                    onClick={() => handlePlanSelection(plan)}
                  >
                    <div className="flex items-center space-x-2 md:space-x-4 flex-1 min-w-0">
                      <button
                        onClick={(e) => handlePlanDetails(plan, e)}
                        className="px-2 md:px-3 py-1 md:py-1.5 bg-blue-100 text-black rounded-2xl md:rounded-3xl text-xs font-medium hover:bg-blue-200 transition-colors whitespace-nowrap"
                      >
                        <span className="hidden sm:inline">{t('countryDetails.plan.details')}</span>
                        <span className="sm:hidden">{t('countryDetails.plan.detailsShort')}</span>
                      </button>
                      <div className="flex items-center space-x-2 md:space-x-3 min-w-0">
                        {/* Network badge */}
                        <div className="flex items-center justify-center w-8 md:w-10 h-5 md:h-6 bg-gray-100 rounded">
                          <span className="text-xs font-bold text-black">{t('countryDetails.plan.networkBadge')}</span>
                        </div>
                        <div className="min-w-0">
                          <div className="text-base md:text-lg font-bold text-gray-900 truncate">
                            {plan.isUnlimited
                              ? t('countryDetails.plan.unlimited')
                              : `${(plan.data_amount / 1000).toFixed(1)} ${t('countryDetails.plan.gb')}`}
                          </div>
                          <div className="text-xs md:text-sm text-gray-600 truncate"> 
                            {t('browse.plans.for')} {plan.duration} {plan.duration === 1 ? t('browse.plans.day').toUpperCase() : t('browse.plans.days').toUpperCase()}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
                      <div className="text-right">
                        <div className="text-lg md:text-xl font-bold text-orange-500">
                          <span className="text-xs md:text-sm text-black mr-1 md:mr-2">{t('countryDetails.currency.usd')}</span>{' '}
                          {plan.finalPrice.toFixed(2)}
                        </div>
                      </div>
                      <div
                        className={`w-5 md:w-6 h-5 md:h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          selectedPlan &&
                          selectedPlan.bundle_name === plan.bundle_name &&
                          selectedPlan.duration === plan.duration
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}
                      >
                        {selectedPlan &&
                          selectedPlan.bundle_name === plan.bundle_name &&
                          selectedPlan.duration === plan.duration && (
                            <div className="w-1.5 md:w-2 h-1.5 md:h-2 bg-white rounded-full"></div>
                          )}
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>

            {/* Show more/less */}
            {(() => {
              const allPlans = getCountryPlans(country);
              const filteredPlans = filterPlansByDuration(allPlans);

              if (filteredPlans.length > 6) {
                return (
                  <div className="text-center">
                    <button
                      onClick={() => setShowAllPlans(!showAllPlans)}
                      className="inline-flex items-center px-4 md:px-6 py-2 md:py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors text-sm md:text-base"
                    > 
                      {showAllPlans ? (
                        <>
                          <span className="hidden sm:inline">{t('countryDetails.more.less')}</span>
                          <span className="sm:hidden">{t('countryDetails.more.lessShort')}</span>
                          <ChevronUp className="w-4 h-4 ml-2" />
                        </>
                      ) : (
                        <>
                          <span className="hidden sm:inline">
                            {t('countryDetails.more.more', { extra: filteredPlans.length - 6 })}
                          </span>
                          <span className="sm:hidden"> 
                            {t('countryDetails.more.moreShort', { extra: filteredPlans.length - 6 })}
                          </span>
                          <ChevronDown className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </button>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        </div>

        {/* Bottom purchase bar */}
        {selectedPlan && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 md:p-4 shadow-lg z-50">
            <div className="max-w-7xl mx-auto">
              {/* Mobile */}
              <div className="flex flex-col sm:hidden space-y-3">
                <div className="flex items-center space-x-3">
                  <Globe className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-gray-900 text-sm truncate">
                      {t('countryDetails.bar.title', { country: country.name })}
                    </div>
                    <div className="text-xs text-gray-600 truncate">
                      {selectedPlan.isUnlimited
                        ? t('countryDetails.plan.unlimited')
                        : `${(selectedPlan.data_amount / 1000).toFixed(1)} ${t('countryDetails.plan.gb')}`}{' '}
                      • {t('countryDetails.duration.days', { count: selectedPlan.duration })}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs text-gray-600">{t('countryDetails.bar.total')}</div>
                    <div className="text-lg font-bold text-orange-500">
                      {t('countryDetails.currency.usd')} {selectedPlan.finalPrice.toFixed(2)}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handlePurchase(selectedPlan)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors w-full"
                >
                  {t('countryDetails.bar.buy')}
                </button>
              </div>

              {/* Desktop */}
              <div className="hidden sm:flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Globe className="w-8 h-8 text-blue-600" />
                  <div>
                    <div className="font-semibold text-gray-900">
                      {t('countryDetails.bar.title', { country: country.name })}
                    </div>
                    <div className="text-sm text-gray-600">
                      {selectedPlan.isUnlimited
                        ? t('countryDetails.plan.unlimited')
                        : `${(selectedPlan.data_amount / 1000).toFixed(1)} ${t('countryDetails.plan.gb')}`}{' '}
                      • {t('countryDetails.duration.days', { count: selectedPlan.duration })}
                    </div>
                  </div>
                </div>
 
                <div className="flex items-center space-x-6">
                  <div className="text-right">
                    <div className="text-sm text-gray-600">{t('countryDetails.bar.total')}</div>
                    <div className="text-xl font-bold text-orange-500">
                      {t('countryDetails.currency.usd')} {selectedPlan.finalPrice.toFixed(2)}
                    </div>
                  </div>
                  <button
                    onClick={() => handlePurchase(selectedPlan)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-semibold transition-colors"
                  >
                    {t('countryDetails.bar.buy')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Plan Details Modal */}
      {showPlanDetailsModal && <PlanDetailsModal />}
    </div>
  );
};

export default CountryDetails;
